import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  penaltiesAPI,
  useApprovePenalty,
  useApplyChecklistPenalty,
  useDeletePenalty,
  useEmployees,
  usePenalties,
  usePenaltyPresets,
} from '@/api';
import { Badge, Button, Card, CardContent, Input } from '@/components/ui';
import { useBranchScope } from '@/context/BranchScopeContext';
import { LoadingSpinner, Pagination } from '@/components/common';
import { AlertTriangle, Check, ChevronDown, Clock3, DollarSign, Download, Plus, RotateCcw, Search, Trash2, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import { downloadBlob } from '@/utils/download';
import { formatAttendanceTime } from '../utils/datetime';
import { extractPaginatedData } from '@/utils/pagination';

const toDay = () => new Date().toISOString().slice(0, 10);
const FILTER_SELECT_CLASS = 'min-h-[2.5rem] w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-[13px] text-slate-700 shadow-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/15';

const getEmployeeName = (employee: any) => employee?.full_name || employee?.name || `Сотрудник #${employee?.id ?? ''}`;
const getEmployeeMeta = (employee: any) => [employee?.employee_id, employee?.email].filter(Boolean).join(' · ');

const STATUS_MAP: Record<string, { variant: string; label: string }> = {
  pending_approval: { variant: 'warning', label: 'Ожидает подтверждения' },
  auto_generated: { variant: 'warning', label: 'Авто-создан' },
  approved: { variant: 'success', label: 'Подтверждён' },
  rejected: { variant: 'destructive', label: 'Отклонён' },
  overridden: { variant: 'default', label: 'Изменён' },
  cancelled: { variant: 'secondary', label: 'Возвращён' },
  paid: { variant: 'default', label: 'Оплачен' },
};

const TYPE_MAP: Record<string, { bg: string; text: string; label: string; icon: string }> = {
  late: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Опоздание', icon: '⏰' },
  early_leave: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Ранний уход', icon: '🚪' },
  absent: { bg: 'bg-red-100', text: 'text-red-800', label: 'Прогул', icon: '❌' },
  no_show: { bg: 'bg-red-100', text: 'text-red-800', label: 'Не явился', icon: '❌' },
  no_checkout: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Не отметил уход', icon: '🔓' },
  no_checkin: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Не отметил приход', icon: '🔓' },
  manual: { bg: 'bg-slate-100', text: 'text-slate-800', label: 'Ручной штраф', icon: '✏️' },
};

const formatClock = (value: string | null | undefined) => {
  if (!value) return null;

  if (/^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
    return value.slice(0, 5);
  }

  return formatAttendanceTime(value, 'ru-RU');
};

const formatPenaltyDate = (value: string | null | undefined) => {
  if (!value) return '—';

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${day}.${month}.${year}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('ru-RU');
};

const RU_WEEKDAY = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
const RU_MONTH_SHORT = ['янв','фев','мар','апр','мая','июн','июл','авг','сен','окт','ноя','дек'];
const formatPenaltyDateRich = (value: string | null | undefined) => {
  if (!value) return { full: '—', weekday: '', short: '—' };
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return {
      full: `${m[3]}.${m[2]}.${m[1]}`,
      weekday: RU_WEEKDAY[d.getDay()] ?? '',
      short: `${Number(m[3])} ${RU_MONTH_SHORT[Number(m[2]) - 1]}`,
    };
  }
  return { full: value, weekday: '', short: value };
};

const getPenaltyEventTime = (penalty: any) => {
  const timesheet = penalty.timesheet;

  switch (penalty.penalty_type) {
    case 'late':
      return formatClock(timesheet?.first_in_at) ?? formatClock(penalty.created_at);
    case 'early_leave':
      return formatClock(timesheet?.last_out_at) ?? formatClock(penalty.created_at);
    case 'no_checkout':
      return formatClock(timesheet?.last_out_at) ?? formatClock(timesheet?.planned_end_time) ?? formatClock(penalty.created_at);
    case 'no_checkin':
      return formatClock(timesheet?.first_in_at) ?? formatClock(timesheet?.planned_start_time) ?? formatClock(penalty.created_at);
    case 'absent':
    case 'no_show':
      return formatClock(timesheet?.planned_start_time) ?? formatClock(penalty.created_at);
    default:
      return formatClock(penalty.created_at);
  }
};

const getPenaltyReasonUz = (penalty: any) => {
  const timesheet = penalty.timesheet;
  const eventTime = getPenaltyEventTime(penalty);
  const lateMinutes = penalty.late_minutes ?? timesheet?.late_minutes;
  const earlyLeaveMinutes = timesheet?.early_leave_minutes;
  const plannedStart = formatClock(timesheet?.planned_start_time);
  const plannedEnd = formatClock(timesheet?.planned_end_time);
  const factIn = formatClock(timesheet?.first_in_at);
  const factOut = formatClock(timesheet?.last_out_at);

  const baseReason = (() => {
    if (penalty.reason && penalty.penalty_type !== 'manual') {
      return penalty.reason;
    }

    switch (penalty.penalty_type) {
      case 'late':
        return lateMinutes ? `Опоздание на ${lateMinutes} мин.` : 'Опоздание.';
      case 'early_leave':
        return earlyLeaveMinutes ? `Ранний уход на ${earlyLeaveMinutes} мин.` : 'Ранний уход.';
      case 'absent':
        return 'Нет записи посещения.';
      case 'no_show':
        return 'Не явился на смену.';
      case 'no_checkout':
        return `Нет отметки ухода${eventTime ? ' (' + eventTime + ')' : ''}.`;
      case 'no_checkin':
        return `Нет отметки прихода${eventTime ? ' (' + eventTime + ')' : ''}.`;
      case 'manual':
        return penalty.reason || penalty.notes || 'Штраф назначен вручную.';
      default:
        return penalty.reason || 'Причина штрафа не указана.';
    }
  })();

  const generatedReason = (() => {
    switch (penalty.penalty_type) {
      case 'late':
        return lateMinutes ? `Опоздание на ${lateMinutes} мин.` : 'Опоздание.';
      case 'early_leave':
        return earlyLeaveMinutes ? `Ранний уход на ${earlyLeaveMinutes} мин.` : 'Ранний уход.';
      case 'absent':
        return 'Нет записи посещения.';
      case 'no_show':
        return 'Не явился на смену.';
      case 'no_checkout':
        return `Нет отметки ухода${eventTime ? ' (' + eventTime + ')' : ''}.`;
      case 'no_checkin':
        return `Нет отметки прихода${eventTime ? ' (' + eventTime + ')' : ''}.`;
      default:
        return null;
    }
  })();

  const details = [
    generatedReason && generatedReason !== baseReason ? generatedReason : null,
    plannedStart || plannedEnd ? `План: ${plannedStart || '—'}–${plannedEnd || '—'}` : null,
    factIn || factOut ? `Факт: ${factIn || '—'}–${factOut || '—'}` : null,
    penalty.notes ? `Комментарий: ${penalty.notes}` : null,
    penalty.approval_notes ? `Комментарий к решению: ${penalty.approval_notes}` : null,
    penalty.override_reason
      ? `${penalty.status === 'cancelled' ? 'Причина возврата' : 'Причина изменения'}: ${penalty.override_reason}`
      : null,
  ].filter(Boolean) as string[];

  return {
    primary: baseReason,
    details: Array.from(new Set(details)),
  };
};

function StatusBadge({ status }: { status: string }) {
  const info = STATUS_MAP[status] || { variant: 'secondary', label: status };
  return <Badge variant={info.variant as any}>{info.label}</Badge>;
}

function TypeBadge({ type }: { type: string }) {
  const info = TYPE_MAP[type] || { bg: 'bg-gray-100', text: 'text-gray-800', label: type, icon: '•' };
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${info.bg} ${info.text}`}>
      <span aria-hidden>{info.icon}</span>
      {info.label}
    </span>
  );
}

function PenaltyActionButtons({
  penalty,
  canManage,
  canApprove,
  onApprove,
  onReject,
  onOverride,
  onRefund,
  onDelete,
}: {
  penalty: any;
  canManage: boolean;
  canApprove: boolean;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  onOverride: (penalty: any) => void;
  onRefund: (id: number) => void;
  onDelete: (penalty: any) => void;
}) {
  const canDeletePenalty = canManage && !['approved', 'overridden', 'cancelled'].includes(penalty.status);
  const iconButtonClass = 'h-8 w-8 min-w-[2.3rem] justify-center px-0';

  return (
    <div className="flex items-center justify-center gap-1.5">
      {['pending_approval', 'auto_generated'].includes(penalty.status) && (canManage || canApprove) && (
        <>
          {canApprove && (
            <Button size="sm" className={iconButtonClass} onClick={() => onApprove(penalty.id)}>
              <Check className="h-4 w-4" />
            </Button>
          )}
          {(canManage || canApprove) && (
            <Button size="sm" variant="outline" className={iconButtonClass} onClick={() => onReject(penalty.id)}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </>
      )}
      {penalty.status === 'approved' && canApprove && (
        <>
          <Button size="sm" variant="outline" className={iconButtonClass} onClick={() => onOverride(penalty)}>
            <DollarSign className="h-4 w-4" />
          </Button>
        </>
      )}
      {['approved', 'overridden'].includes(penalty.status) && canApprove && (
        <>
          {Number(penalty.amount || 0) > 0 && (
            <Button size="sm" variant="outline" className={iconButtonClass} onClick={() => onRefund(penalty.id)}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
        </>
      )}
      {canDeletePenalty && (
        <Button
          size="sm"
          variant="outline"
          className={`${iconButtonClass} border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700`}
          onClick={() => onDelete(penalty)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function PenaltyMetricCard({
  icon,
  label,
  value,
  helper,
  iconClassName,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  helper?: string;
  iconClassName: string;
}) {
  return (
    <Card className="border-slate-200/90 bg-white/95">
      <CardContent className="flex items-center gap-3 p-4">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconClassName}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</p>
          <p className="mt-1 text-xl font-bold tracking-tight text-slate-900 sm:text-[1.4rem]">{value}</p>
          {helper && <p className="mt-1 text-[11px] leading-4 text-slate-500">{helper}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function AssignPenaltyModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const navigate = useNavigate();
  const [employeeId, setEmployeeId] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [employeeFocusIndex, setEmployeeFocusIndex] = useState(0);
  const [workDate, setWorkDate] = useState(toDay());
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [presetAmounts, setPresetAmounts] = useState<Record<number, string>>({});
  const employeePickerRef = React.useRef<HTMLDivElement | null>(null);

  const { data: employeesData } = useEmployees({ per_page: 1000 });
  const { data: presetsData } = usePenaltyPresets({ is_active: true });
  const applyChecklist = useApplyChecklistPenalty();

  const employees = Array.isArray(employeesData?.data)
    ? employeesData.data
    : employeesData?.data?.data || [];

  const allPresets: any[] = Array.isArray(presetsData?.data)
    ? presetsData.data
    : presetsData?.data?.data || [];

  const selectedEmployee = useMemo(
    () => employees.find((employee: any) => String(employee.id) === employeeId) ?? null,
    [employeeId, employees]
  );

  const filteredPresets = useMemo(() => {
    if (!search.trim()) {
      return allPresets;
    }

    const query = search.toLowerCase();

    return allPresets.filter(
      (preset: any) =>
        preset.name?.toLowerCase().includes(query) ||
        preset.description?.toLowerCase().includes(query) ||
        preset.code?.toLowerCase().includes(query)
    );
  }, [allPresets, search]);

  const filteredEmployees = useMemo(() => {
    const query = employeeSearch.trim().toLowerCase();

    const list = query
      ? employees.filter((employee: any) =>
          [getEmployeeName(employee), employee.employee_id, employee.email]
            .filter(Boolean)
            .some((value: string) => value.toLowerCase().includes(query))
        )
      : employees;

    if (selectedEmployee && !list.some((employee: any) => employee.id === selectedEmployee.id)) {
      return [selectedEmployee, ...list];
    }

    return list;
  }, [employeeId, employeeSearch, employees]);

  React.useEffect(() => {
    if (!employeeDropdownOpen) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (employeePickerRef.current && !employeePickerRef.current.contains(target)) {
        setEmployeeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [employeeDropdownOpen]);

  React.useEffect(() => {
    setEmployeeFocusIndex(0);
  }, [employeeSearch, employeeDropdownOpen]);

  const selectedPresets = useMemo(
    () => allPresets.filter((preset: any) => selectedIds.includes(preset.id)),
    [allPresets, selectedIds]
  );

  const getAppliedAmount = (preset: any) => {
    const rawValue = presetAmounts[preset.id]?.trim();

    if (!rawValue) {
      return Number(preset.amount || 0);
    }

    const parsedValue = Number(rawValue);
    return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : Number.NaN;
  };

  const hasInvalidManualAmounts = selectedPresets.some((preset: any) => Number.isNaN(getAppliedAmount(preset)));

  const total = selectedPresets.reduce((sum, preset) => {
    const amount = getAppliedAmount(preset);
    return sum + (Number.isNaN(amount) ? 0 : amount);
  }, 0);

  const toggle = (id: number) => {
    const isSelected = selectedIds.includes(id);

    setSelectedIds((previous) =>
      isSelected ? previous.filter((value) => value !== id) : [...previous, id]
    );

    if (isSelected) {
      setPresetAmounts((previous) => {
        const next = { ...previous };
        delete next[id];
        return next;
      });
    }
  };

  const reset = () => {
    setEmployeeId('');
    setEmployeeSearch('');
    setEmployeeDropdownOpen(false);
    setEmployeeFocusIndex(0);
    setWorkDate(toDay());
    setNotes('');
    setSearch('');
    setSelectedIds([]);
    setPresetAmounts({});
  };

  const handleEmployeeSelect = (employee: any) => {
    setEmployeeId(String(employee.id));
    setEmployeeSearch(getEmployeeName(employee));
    setEmployeeDropdownOpen(false);
    setEmployeeFocusIndex(0);
  };

  const handleEmployeeSearchChange = (value: string) => {
    setEmployeeSearch(value);
    setEmployeeDropdownOpen(true);

    if (selectedEmployee && value !== getEmployeeName(selectedEmployee)) {
      setEmployeeId('');
    }
  };

  const handleEmployeeKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setEmployeeDropdownOpen(false);
      return;
    }

    if (!filteredEmployees.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setEmployeeDropdownOpen(true);
      setEmployeeFocusIndex((previous) => Math.min(previous + 1, filteredEmployees.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setEmployeeDropdownOpen(true);
      setEmployeeFocusIndex((previous) => Math.max(previous - 1, 0));
      return;
    }

    if (event.key === 'Enter' && employeeDropdownOpen) {
      event.preventDefault();
      handleEmployeeSelect(filteredEmployees[employeeFocusIndex]);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleOpenPresets = () => {
    handleClose();
    navigate('/penalty-presets');
  };

  const handleSubmit = async () => {
    if (!employeeId) {
      window.alert('Выберите сотрудника.');
      return;
    }

    if (!selectedIds.length) {
      window.alert('Выберите хотя бы один тип штрафа.');
      return;
    }

    if (hasInvalidManualAmounts) {
      window.alert('Проверьте вручную введённые суммы.');
      return;
    }

    const presetAmountsPayload = selectedPresets.reduce((accumulator, preset) => {
      const rawValue = presetAmounts[preset.id]?.trim();

      if (!rawValue) {
        return accumulator;
      }

      accumulator[preset.id] = Number(rawValue);
      return accumulator;
    }, {} as Record<number, number>);

    await applyChecklist.mutateAsync({
      employee_id: Number(employeeId),
      work_date: workDate,
      preset_ids: selectedIds,
      preset_amounts: Object.keys(presetAmountsPayload).length ? presetAmountsPayload : undefined,
      notes: notes || undefined,
    });

    reset();
    onSuccess();
  };

  return (
    <div
      className={`fixed inset-0 z-50 overflow-y-auto bg-black/50 p-3 transition-opacity sm:p-4 ${
        isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
      }`}
    >
      <div className="mx-auto flex max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl sm:max-h-[95vh]">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-4 py-3 sm:px-6 sm:py-4">
          <h2 className="text-lg font-semibold">Назначить штраф сотруднику</h2>
          <button onClick={handleClose} className="rounded-lg p-1 text-gray-500 hover:bg-gray-100">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-3 sm:space-y-5 sm:px-6 sm:py-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Сотрудник</label>
              <div ref={employeePickerRef} className="relative">
                <div
                  className={`flex min-h-[2.75rem] items-center rounded-xl border bg-white px-3 shadow-sm transition ${
                    employeeDropdownOpen
                      ? 'border-blue-400 ring-2 ring-blue-500/15'
                      : 'border-slate-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-500/15'
                  }`}
                >
                  <Search size={16} className="mr-2 shrink-0 text-slate-400" />
                  <input
                    className="h-full min-w-0 flex-1 border-0 bg-transparent py-2.5 text-sm text-slate-800 outline-none placeholder:text-slate-400"
                    placeholder="Поиск по имени, ID или email"
                    value={employeeSearch}
                    onFocus={() => setEmployeeDropdownOpen(true)}
                    onChange={(e) => handleEmployeeSearchChange(e.target.value)}
                    onKeyDown={handleEmployeeKeyDown}
                    aria-expanded={employeeDropdownOpen}
                    aria-autocomplete="list"
                    aria-controls="employee-combobox-list"
                  />
                  {employeeSearch && (
                    <button
                      type="button"
                      className="mr-1 rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                      onClick={() => {
                        setEmployeeId('');
                        setEmployeeSearch('');
                        setEmployeeDropdownOpen(true);
                        setEmployeeFocusIndex(0);
                      }}
                      aria-label="Очистить сотрудника"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                    onClick={() => setEmployeeDropdownOpen((previous) => !previous)}
                    aria-label="Открыть список сотрудников"
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${employeeDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                {employeeDropdownOpen && (
                  <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/70">
                    <div id="employee-combobox-list" className="max-h-64 overflow-y-auto py-1">
                      {filteredEmployees.length === 0 ? (
                        <p className="px-4 py-6 text-center text-sm text-slate-400">Сотрудники не найдены</p>
                      ) : (
                        filteredEmployees.map((employee: any, index: number) => {
                          const isActive = index === employeeFocusIndex;
                          const isSelected = String(employee.id) === employeeId;

                          return (
                            <button
                              key={employee.id}
                              type="button"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => handleEmployeeSelect(employee)}
                              className={`flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition ${
                                isActive || isSelected ? 'bg-slate-100' : 'hover:bg-slate-50'
                              }`}
                            >
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-slate-800">{getEmployeeName(employee)}</p>
                                {getEmployeeMeta(employee) && (
                                  <p className="mt-0.5 truncate text-xs text-slate-500">{getEmployeeMeta(employee)}</p>
                                )}
                              </div>
                              {isSelected && <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

                <p className="mt-1.5 text-[11px] text-slate-400">
                  {selectedEmployee
                    ? `Выбран: ${getEmployeeName(selectedEmployee)}${getEmployeeMeta(selectedEmployee) ? ` · ${getEmployeeMeta(selectedEmployee)}` : ''}`
                    : 'Начните вводить имя сотрудника и выберите его из списка.'}
                </p>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Дата</label>
              <Input type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Типы штрафов{' '}
              <span className="text-xs font-normal text-gray-400">({allPresets.length})</span>
            </label>

            {allPresets.length === 0 ? (
              <div className="rounded-xl border border-dashed bg-gray-50 px-4 py-6 text-center">
                <p className="text-sm text-gray-600">
                  Типы штрафов еще не созданы. Сначала добавьте их в отдельном разделе.
                </p>
                <Button className="mt-3" variant="outline" onClick={handleOpenPresets}>
                  Открыть раздел «Типы штрафов»
                </Button>
              </div>
            ) : (
              <>
                <div className="relative mb-2">
                  <Search
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    className="w-full rounded-lg border py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Поиск по названию, коду или описанию"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div className="max-h-64 overflow-y-auto rounded-xl border divide-y">
                  {filteredPresets.length === 0 && (
                    <p className="py-6 text-center text-sm text-gray-400">Ничего не найдено</p>
                  )}
                  {filteredPresets.map((preset: any) => {
                    const checked = selectedIds.includes(preset.id);

                    return (
                      <label
                        key={preset.id}
                        className={`flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors ${
                          checked ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(preset.id)}
                          className="h-4 w-4 rounded border-gray-300 accent-blue-600"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium break-words">{preset.name}</p>
                          {preset.description && (
                            <p className="text-xs text-gray-500 break-words">{preset.description}</p>
                          )}
                        </div>
                        <span
                          className={`shrink-0 rounded-full px-3 py-0.5 text-xs font-semibold ${
                            checked ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {Number(preset.amount || 0).toLocaleString('ru-RU')}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {selectedPresets.length > 0 && (
            <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700">
                Выбранные штрафы
              </p>

              <div className="mt-2.5 max-h-72 space-y-2 overflow-y-auto pr-1">
                {selectedPresets.map((preset: any) => {
                  const appliedAmount = getAppliedAmount(preset);
                  const isInvalidAmount = Number.isNaN(appliedAmount);

                  return (
                    <div key={preset.id} className="rounded-lg border border-white/90 bg-white px-3 py-2.5">
                      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-semibold leading-5 text-slate-800">{preset.name}</p>
                          <p className="mt-0.5 text-[11px] text-slate-400">
                            {Number(preset.amount || 0).toLocaleString('ru-RU')} сум
                          </p>
                        </div>

                        <div className="flex items-center gap-2 sm:justify-end">
                          <div className="relative w-full sm:w-[148px]">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder={String(Number(preset.amount || 0))}
                              className="h-9 pr-10 text-right text-[13px] font-medium"
                              value={presetAmounts[preset.id] ?? ''}
                              onChange={(e) =>
                                setPresetAmounts((previous) => ({
                                  ...previous,
                                  [preset.id]: e.target.value,
                                }))
                              }
                            />
                            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[10px] text-slate-400">
                              сум
                            </span>
                          </div>
                          <div className="min-w-[72px] text-right">
                            <p className={`text-[13px] font-semibold ${isInvalidAmount ? 'text-red-600' : 'text-slate-800'}`}>
                              {isInvalidAmount ? 'Ошибка' : appliedAmount.toLocaleString('ru-RU')}
                            </p>
                          </div>
                        </div>
                      </div>

                      {isInvalidAmount && (
                        <p className="mt-1.5 text-[10px] text-red-600">Введите корректную сумму.</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Комментарий (необязательно)
            </label>
            <textarea
              className="min-h-[72px] w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Дополнительный комментарий"
            />
          </div>
        </div>

        <div className="flex flex-col gap-3 rounded-b-2xl border-t bg-gray-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <p className="text-xs text-gray-500">Общая сумма</p>
            <p className="text-2xl font-bold text-red-600">
              {total.toLocaleString('ru-RU')}{' '}
              <span className="text-sm font-normal text-gray-500">сум</span>
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <Button variant="outline" onClick={handleClose} className="w-full sm:w-auto">
              Отмена
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={applyChecklist.isPending || !selectedIds.length || hasInvalidManualAmounts}
              className="w-full sm:w-auto"
            >
              {applyChecklist.isPending ? 'Сохранение...' : 'Назначить штраф'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Override Modal ───────────────────────────────────────────────────────────
function OverrideModal({
  penalty,
  onClose,
  onConfirm,
}: {
  penalty: any;
  onClose: () => void;
  onConfirm: (amount: number, reason: string) => Promise<void>;
}) {
  const [amount, setAmount] = useState(String(Number(penalty.amount || 0)));
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    const num = Number(amount);
    if (!Number.isFinite(num) || num < 0) { setError('Введите корректную сумму'); return; }
    if (!reason.trim()) { setError('Укажите причину изменения'); return; }
    setError(''); setLoading(true);
    try {
      await onConfirm(num, reason.trim());
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Произошла ошибка');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-3 sm:p-4">
      <div className="mx-auto w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-4 py-4 sm:px-6">
          <h2 className="text-lg font-semibold">Изменить сумму штрафа</h2>
          <button onClick={onClose} className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"><X size={20} /></button>
        </div>
        <div className="space-y-4 px-4 py-5 sm:px-6">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Новая сумма</label>
            <Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Причина изменения</label>
            <textarea
              className="min-h-[80px] w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Укажите причину изменения суммы"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex flex-col gap-3 border-t px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">Отмена</Button>
          <Button onClick={handleSubmit} disabled={loading} className="w-full sm:w-auto">{loading ? 'Сохранение...' : 'Сохранить'}</Button>
        </div>
      </div>
    </div>
  );
}

// ─── Refund Modal ─────────────────────────────────────────────────────────────
function RefundModal({
  onClose,
  onConfirm,
}: {
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!reason.trim()) { setError('Укажите причину возврата'); return; }
    setError(''); setLoading(true);
    try {
      await onConfirm(reason.trim());
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Произошла ошибка');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-3 sm:p-4">
      <div className="mx-auto w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-4 py-4 sm:px-6">
          <h2 className="text-lg font-semibold">Возврат штрафа</h2>
          <button onClick={onClose} className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"><X size={20} /></button>
        </div>
        <div className="space-y-4 px-4 py-5 sm:px-6">
          <p className="text-sm text-gray-600">Сумма штрафа будет обнулена. Укажите причину.</p>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Причина возврата</label>
            <textarea
              className="min-h-[80px] w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Причина возврата штрафа"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex flex-col gap-3 border-t px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">Отмена</Button>
          <Button className="w-full bg-red-600 text-white hover:bg-red-700 sm:w-auto" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Обработка...' : 'Возврат'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm Modal ────────────────────────────────────────────────────────────
function ConfirmModal({
  message,
  onClose,
  onConfirm,
}: {
  message: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [loading, setLoading] = useState(false);
  const handleConfirm = async () => {
    setLoading(true);
    try { await onConfirm(); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-3 sm:p-4">
      <div className="mx-auto w-full max-w-sm rounded-2xl bg-white shadow-2xl">
        <div className="px-4 py-6 sm:px-6">
          <p className="text-base font-medium text-gray-800">{message}</p>
        </div>
        <div className="flex flex-col gap-3 border-t px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">Отмена</Button>
          <Button className="w-full bg-red-600 text-white hover:bg-red-700 sm:w-auto" onClick={handleConfirm} disabled={loading}>
            {loading ? 'Обработка...' : 'Подтвердить'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PenaltiesPage() {
  const perPage = 10;
  const { can } = useAuth();
  const canManage = can('manage_penalties');
  const canApprove = can('approve_penalties');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('active');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState<any>(null);
  const [refundTarget, setRefundTarget] = useState<number | null>(null);
  const [rejectTarget, setRejectTarget] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  const { branchParam } = useBranchScope();

  const penaltyQueryParams = useMemo(() => ({
    page,
    per_page: perPage,
    employee_id: employeeFilter,
    branch_id: branchParam,
    status: statusFilter === 'all' ? '' : statusFilter,
    date_from: dateFrom,
    date_to: dateTo,
  }), [page, perPage, employeeFilter, branchParam, statusFilter, dateFrom, dateTo]);

  const { data: penaltiesData, isLoading, refetch } = usePenalties(penaltyQueryParams);
  const { data: employeesData } = useEmployees({ per_page: 1000, include_inactive: true });
  const approveMutation = useApprovePenalty();
  const deleteMutation = useDeletePenalty();

  const employees = Array.isArray(employeesData?.data)
    ? employeesData.data
    : employeesData?.data?.data || [];

  const { items: penalties, meta } = extractPaginatedData<any>(penaltiesData, page, perPage);

  const totals = useMemo(() => {
    const backendSummary = penaltiesData?.summary;
    const overallSummary = penaltiesData?.overall_summary ?? backendSummary;
    const approved = penalties.filter((p) => ['approved', 'overridden'].includes(p.status));
    const pending = penalties.filter((p) => ['pending_approval', 'auto_generated'].includes(p.status));
    const rejected = penalties.filter((p) => p.status === 'rejected');
    const cancelled = penalties.filter((p) => p.status === 'cancelled');

    return {
      totalCount: Number(backendSummary?.total_count ?? meta.total ?? penalties.length),
      totalAmount: Number(backendSummary?.total_amount ?? penalties.reduce((sum, p) => sum + Number(p.amount || 0), 0)),
      approvedAmount: Number(overallSummary?.approved_amount ?? approved.reduce((sum, p) => sum + Number(p.amount || 0), 0)),
      pendingAmount: Number(overallSummary?.pending_amount ?? pending.reduce((sum, p) => sum + Number(p.amount || 0), 0)),
      cancelledAmount: Number(overallSummary?.cancelled_amount ?? cancelled.reduce((sum, p) => sum + Number(p.amount || 0), 0)),
      pendingCount: Number(overallSummary?.pending_count ?? pending.length),
      approvedCount: Number(overallSummary?.approved_count ?? approved.length),
      rejectedCount: Number(overallSummary?.rejected_count ?? rejected.length),
      cancelledCount: Number(overallSummary?.cancelled_count ?? cancelled.length),
    };
  }, [meta.total, penalties, penaltiesData]);

  const handleApprove = (id: number) => approveMutation.mutateAsync(id);

  const handleOverride = (penalty: any) => setOverrideTarget(penalty);
  const handleRefund = (id: number) => setRefundTarget(id);
  const handleReject = (id: number) => setRejectTarget(id);
  const handleDelete = (penalty: any) => setDeleteTarget(penalty);

  const handleExport = async () => {
    try {
      const blob = await penaltiesAPI.export({
        employee_id: employeeFilter,
        status: statusFilter === 'all' ? '' : statusFilter,
        date_from: dateFrom,
        date_to: dateTo,
      });

      downloadBlob(blob, `penalties_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Excel-файл выгружен');
    } catch (error: any) {
      toast.error(error?.message || 'Ошибка при скачивании');
    }
  };

  const confirmOverride = async (amount: number, reason: string) => {
    await penaltiesAPI.override(overrideTarget.id, { amount, override_reason: reason });
    toast.success('Сумма штрафа обновлена');
    setOverrideTarget(null);
    refetch();
  };

  const confirmRefund = async (reason: string) => {
    const response: any = await penaltiesAPI.refund(refundTarget!, { reason });
    toast.success(response?.data?.message || 'Штраф возвращен');
    setRefundTarget(null);
    refetch();
  };

  const confirmReject = async () => {
    await penaltiesAPI.reject(rejectTarget!);
    setRejectTarget(null);
    refetch();
  };

  const confirmDelete = async () => {
    await deleteMutation.mutateAsync(deleteTarget.id);
    toast.success('Штраф удалён');
    setDeleteTarget(null);
    refetch();
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const activeEmployeeLabel = employeeFilter
    ? employees.find((employee: any) => String(employee.id) === employeeFilter)?.full_name || 'Выбранный сотрудник'
    : 'Все сотрудники';

  return (
    <div className="penalty-compact-page space-y-5 pb-6 page-shell">
      <Card className="overflow-hidden border-slate-200/90 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.2),_transparent_38%),linear-gradient(135deg,_rgba(255,255,255,0.98),_rgba(239,246,255,0.96))]">
        <CardContent className="p-4 sm:p-5 lg:p-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600/70">Контроль штрафов</p>
              <h1 className="mt-2 text-[1.9rem] font-bold tracking-tight text-slate-950 sm:text-[2.25rem]">Штрафы сотрудников</h1>
              <p className="mt-2 max-w-2xl text-[13px] leading-5 text-slate-600 sm:text-sm">
                Управляйте штрафами в одном месте: фильтруйте, подтверждайте, экспортируйте и добавляйте записи вручную.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full border border-white/70 bg-white/80 px-3 py-1 text-xs text-slate-700 shadow-sm">
                  Записей: <strong className="text-slate-900">{totals.totalCount}</strong>
                </span>
                <span className="rounded-full border border-white/70 bg-white/80 px-3 py-1 text-xs text-slate-700 shadow-sm">
                  Ожидают подтверждения: <strong className="text-slate-900">{totals.pendingCount}</strong>
                </span>
                <span className="rounded-full border border-white/70 bg-white/80 px-3 py-1 text-xs text-slate-700 shadow-sm">
                  Охват: <strong className="text-slate-900">{activeEmployeeLabel}</strong>
                </span>
              </div>
            </div>

            <div className="flex w-full flex-col gap-3 sm:flex-row xl:w-auto">
              <Button size="sm" variant="outline" className="border-white/70 bg-white/85 shadow-sm hover:bg-white" onClick={handleExport}>
                <Download size={16} className="mr-1" />
                Экспорт в Excel
              </Button>
              {canManage && (
                <Button size="sm" className="shadow-sm shadow-blue-500/20" onClick={() => setModalOpen(true)}>
                  <Plus size={16} className="mr-1" />
                  Назначить штраф
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="border-slate-200/90">
        <CardContent className="space-y-3 p-3.5 sm:p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Фильтры</p>
              <h2 className="mt-1 text-base font-semibold text-slate-900">Сортировка и контроль штрафов</h2>
            </div>
            {(employeeFilter || statusFilter !== 'active' || dateFrom || dateTo) && (
              <button
                type="button"
                className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-100 hover:text-slate-900"
                onClick={() => {
                  setEmployeeFilter('');
                  setStatusFilter('active');
                  setDateFrom('');
                  setDateTo('');
                  setPage(1);
                }}
              >
                Очистить фильтры
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Сотрудник</label>
              <select
                className={FILTER_SELECT_CLASS}
                value={employeeFilter}
                onChange={(e) => {
                  setPage(1);
                  setEmployeeFilter(e.target.value);
                }}
              >
                <option value="">Все сотрудники</option>
                {employees.map((emp: any) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.full_name} ({emp.employee_id})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Статус</label>
              <select
                className={FILTER_SELECT_CLASS}
                value={statusFilter}
                onChange={(e) => {
                  setPage(1);
                  setStatusFilter(e.target.value);
                }}
              >
                <option value="active">К удержанию (подтверждённые)</option>
                <option value="pending_approval">Ожидает подтверждения</option>
                <option value="approved">Подтверждён</option>
                <option value="overridden">Изменён</option>
                <option value="cancelled">Возвращён</option>
                <option value="rejected">Отклонён</option>
                <option value="all">Все статусы (история)</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">С даты</label>
              <Input
                type="date"
                className="min-h-[2.5rem] border-slate-200 bg-white px-3.5 text-[13px] shadow-sm focus:border-blue-400 focus:ring-blue-500/15"
                value={dateFrom}
                onChange={(e) => {
                  setPage(1);
                  setDateFrom(e.target.value);
                }}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">По дату</label>
              <Input
                type="date"
                className="min-h-[2.5rem] border-slate-200 bg-white px-3.5 text-[13px] shadow-sm focus:border-blue-400 focus:ring-blue-500/15"
                value={dateTo}
                onChange={(e) => {
                  setPage(1);
                  setDateTo(e.target.value);
                }}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 text-xs text-slate-500">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
              Записей: <strong className="text-slate-900">{totals.totalCount}</strong>
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
              Общая сумма: <strong className="text-slate-900">{totals.totalAmount.toLocaleString('ru-RU')} сум</strong>
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
              Охват: <strong className="text-slate-900">{activeEmployeeLabel}</strong>
            </span>
          </div>
        </CardContent>
      </Card>

      <AssignPenaltyModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          setModalOpen(false);
          refetch();
        }}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <PenaltyMetricCard
          icon={<AlertTriangle className="h-5 w-5 text-amber-600" />}
          iconClassName="bg-amber-100"
          label="Ожидают подтверждения"
          value={totals.pendingCount}
          helper="Авто или ручные записи на проверке"
        />
        <PenaltyMetricCard
          icon={<Check className="h-5 w-5 text-emerald-600" />}
          iconClassName="bg-emerald-100"
          label="Подтверждённые"
          value={totals.approvedCount}
          helper="Активные и подтверждённые штрафы"
        />
        <PenaltyMetricCard
          icon={<X className="h-5 w-5 text-rose-600" />}
          iconClassName="bg-rose-100"
          label="Отклонённые"
          value={totals.rejectedCount}
          helper="Отклонены при проверке"
        />
        <PenaltyMetricCard
          icon={<RotateCcw className="h-5 w-5 text-slate-600" />}
          iconClassName="bg-slate-100"
          label="Возвращённые"
          value={totals.cancelledCount}
          helper="Возвраты не удерживаются"
        />
        <PenaltyMetricCard
          icon={<DollarSign className="h-5 w-5 text-blue-600" />}
          iconClassName="bg-blue-100"
          label="К удержанию"
          value={`${totals.approvedAmount.toLocaleString('ru-RU')} сум`}
          helper="Подтверждённые и изменённые"
        />
      </div>

      <Card className="border-slate-200/90 bg-white/95">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-3.5 py-3 sm:px-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Список</p>
            <h2 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">Записи штрафов</h2>
            <p className="mt-1 text-[12px] text-slate-500">Краткий список по смене, причине и статусу.</p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-slate-500">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
              Подтверждённые: <strong className="text-slate-900">{totals.approvedCount}</strong>
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
              Отклонённые: <strong className="text-slate-900">{totals.rejectedCount}</strong>
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
              Возвращённые: <strong className="text-slate-900">{totals.cancelledCount}</strong>
            </span>
          </div>
        </div>

        <div className="overflow-x-auto ui-table-wrap">
          <table className="w-full ui-table">
            <colgroup>
              <col className="w-[132px]" />
              <col className="w-[196px]" />
              <col className="w-[196px]" />
              <col />
              <col className="w-[120px]" />
              <col className="w-[132px]" />
              <col className="w-[126px]" />
            </colgroup>
            <thead className="border-b bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Дата</th>
                <th className="px-4 py-3 text-left font-semibold">Сотрудник</th>
                <th className="px-4 py-3 text-left font-semibold">Смена / план</th>
                <th className="px-4 py-3 text-left font-semibold">Причина штрафа</th>
                <th className="px-4 py-3 text-right font-semibold">Сумма</th>
                <th className="px-4 py-3 text-center font-semibold">Статус</th>
                <th className="px-4 py-3 text-center font-semibold">Действия</th>
              </tr>
            </thead>
            <tbody>
              {penalties.map((penalty: any) => {
                const reason = getPenaltyReasonUz(penalty);
                const eventTime = getPenaltyEventTime(penalty);
                const createdTime = formatClock(penalty.created_at);
                const employeeName = penalty.employee?.full_name || '—';
                const employeeCode = penalty.employee?.employee_id || null;
                const dateRich = formatPenaltyDateRich(penalty.penalty_date);
                const ts = penalty.timesheet;
                const shiftName = ts?.shift?.name || (ts?.shift_id ? `Смена #${ts.shift_id}` : null);
                const plannedStart = formatClock(ts?.planned_start_time);
                const plannedEnd = formatClock(ts?.planned_end_time);
                const factIn = formatClock(ts?.first_in_at);
                const factOut = formatClock(ts?.last_out_at);
                const lateMin = penalty.late_minutes ?? ts?.late_minutes ?? 0;
                const earlyMin = ts?.early_leave_minutes ?? 0;
                const isAuto = penalty.status === 'auto_generated' || penalty.is_auto_generated;

                return (
                  <tr key={penalty.id} className="border-b align-top hover:bg-blue-50/30 transition-colors">
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-1.5">
                        <div className="text-[15px] font-bold text-slate-900 leading-tight">{dateRich.full}</div>
                        {dateRich.weekday && (
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{dateRich.weekday}</div>
                        )}
                      </div>
                      <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                        <Clock3 className="h-3 w-3" />
                        {eventTime || '—'}
                      </div>
                      {createdTime && createdTime !== eventTime && (
                        <div className="mt-1 text-[10px] text-slate-400">Создан {createdTime}</div>
                      )}
                    </td>

                    <td className="px-4 py-3 align-top">
                      <div className="flex items-start gap-2.5">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 text-[11px] font-bold text-blue-700">
                          {(employeeName[0] ?? '?').toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-[13px] text-slate-900 leading-tight truncate">{employeeName}</div>
                          {employeeCode && <div className="mt-0.5 text-[10px] text-slate-400">ID {employeeCode}</div>}
                          {penalty.employee?.department?.name && (
                            <div className="mt-1 inline-flex max-w-[150px] items-center truncate rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                              {penalty.employee.department.name}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 align-top">
                      {shiftName ? (
                        <>
                          <div className="text-[12px] font-semibold text-slate-800 truncate">{shiftName}</div>
                          {(plannedStart || plannedEnd) && (
                            <div className="mt-1 inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 font-mono text-[10px] font-medium text-blue-700">
                              {plannedStart || '—'} – {plannedEnd || '—'}
                            </div>
                          )}
                          <div className="mt-1 text-[10px] text-slate-500">
                            {(factIn || factOut) ? (
                              <span>
                                {factIn ? <>Вх <span className="font-mono font-medium text-slate-700">{factIn}</span></> : 'Вх —'}
                                {' · '}
                                {factOut ? <>Вых <span className="font-mono font-medium text-slate-700">{factOut}</span></> : 'Вых —'}
                              </span>
                            ) : (
                              <span>Факт: —</span>
                            )}
                          </div>
                        </>
                      ) : (
                        <span className="text-[11px] italic text-slate-400">Смена не назначена</span>
                      )}
                    </td>

                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-col gap-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <TypeBadge type={penalty.penalty_type} />
                          {isAuto && (
                            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                              Авто
                            </span>
                          )}
                          {Number(lateMin) > 0 && (
                            <span className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">
                              +{lateMin} мин
                            </span>
                          )}
                          {Number(earlyMin) > 0 && (
                            <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700">
                              -{earlyMin} мин
                            </span>
                          )}
                        </div>
                        <div className="text-[12px] leading-4 text-slate-700">{reason.primary}</div>
                        {reason.details.length > 0 && (
                          <div className="space-y-0.5 text-[10px] leading-4 text-slate-500">
                            {reason.details.map((line: string, idx: number) => (
                              <div key={`${penalty.id}-reason-${idx}`} className="whitespace-normal break-words">
                                {line}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3 align-top text-right">
                      <div className="text-[15px] font-bold text-slate-900 whitespace-nowrap">
                        {penalty.amount != null ? `${Number(penalty.amount).toLocaleString('ru-RU')} сум` : '—'}
                      </div>
                    </td>

                    <td className="px-4 py-3 align-top text-center">
                      <div className="flex justify-center">
                        <StatusBadge status={penalty.status} />
                      </div>
                    </td>

                    <td className="px-4 py-3 align-top text-center">
                      <PenaltyActionButtons
                        penalty={penalty}
                        canManage={canManage}
                        canApprove={canApprove}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onOverride={handleOverride}
                        onRefund={handleRefund}
                        onDelete={handleDelete}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      <Pagination meta={meta} onPageChange={setPage} />

      {penalties.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white/70 py-12 text-center">
          <AlertTriangle className="mx-auto mb-4 h-16 w-16 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Штрафы не найдены</h3>
          <p className="text-gray-500">По выбранным фильтрам штрафов нет.</p>
        </div>
      )}

      {overrideTarget && (
        <OverrideModal
          penalty={overrideTarget}
          onClose={() => setOverrideTarget(null)}
          onConfirm={confirmOverride}
        />
      )}
      {refundTarget !== null && (
        <RefundModal
          onClose={() => setRefundTarget(null)}
          onConfirm={confirmRefund}
        />
      )}
      {rejectTarget !== null && (
        <ConfirmModal
          message="Отклонить этот штраф?"
          onClose={() => setRejectTarget(null)}
          onConfirm={confirmReject}
        />
      )}
      {deleteTarget !== null && (
        <ConfirmModal
          message="Удалить этот штраф?"
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}
